import { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronDown, Check, X } from "lucide-react";

/**
 * Lightweight searchable select / combobox.
 *
 * Props:
 *  - options: [{ value, label, group? }] — group used to render optgroup-like sections
 *  - value: selected value
 *  - onChange(value)
 *  - placeholder
 *  - testId
 */
export default function Combobox({
  options = [],
  value,
  onChange,
  placeholder = "Select…",
  testId,
  emptyLabel = "No matches",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selectedOption = useMemo(
    () => options.find((o) => o.value === value),
    [options, value],
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => {
      const text = `${o.label} ${o.group || ""} ${o.value || ""}`.toLowerCase();
      return text.includes(needle);
    });
  }, [options, query]);

  // Group filtered by `.group`
  const groups = useMemo(() => {
    const m = new Map();
    for (const o of filtered) {
      const key = o.group || "";
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(o);
    }
    return Array.from(m.entries());
  }, [filtered]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  const select = (val) => {
    onChange(val);
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 border border-zinc-300 px-3 py-2.5 text-sm bg-white text-left hover:border-[#002FA7] focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
        data-testid={testId ? `${testId}-button` : undefined}
      >
        <span className={`flex-1 truncate ${selectedOption ? "text-zinc-900" : "text-zinc-400"}`}>
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown size={14} className="text-zinc-400 shrink-0" />
      </button>
      {open && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 border border-zinc-200 bg-white shadow-lg max-h-80 flex flex-col"
          data-testid={testId ? `${testId}-popover` : undefined}
        >
          <div className="relative border-b border-zinc-100 shrink-0">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to search…"
              className="w-full pl-9 pr-9 py-2.5 text-sm focus:outline-none"
              data-testid={testId ? `${testId}-search` : undefined}
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
                aria-label="Clear search"
              >
                <X size={13} />
              </button>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 ? (
              <div className="px-4 py-6 text-center text-xs text-zinc-500">{emptyLabel}</div>
            ) : (
              groups.map(([groupName, items]) => (
                <div key={groupName || "_default"}>
                  {groupName && (
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b border-zinc-100 sticky top-0">
                      {groupName}
                    </div>
                  )}
                  {items.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => select(o.value)}
                      className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-zinc-50 border-b border-zinc-50 last:border-0 ${
                        o.value === value ? "bg-[#002FA7]/5 text-[#002FA7]" : "text-zinc-800"
                      }`}
                      data-testid={testId ? `${testId}-option-${o.value}` : undefined}
                    >
                      <span className="flex-1 truncate">{o.label}</span>
                      {o.value === value && <Check size={12} className="shrink-0" />}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
